type Loader = () => Promise<unknown>;

interface Scheduler {
  idle(run: () => void): void;
  allowed(): boolean;
}

const browserScheduler: Scheduler = {
  idle(run) {
    if (typeof window === "undefined") return;
    if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 1500 });
    else setTimeout(run, 150);
  },
  allowed() {
    if (typeof navigator === "undefined") return false;
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } })
      .connection;
    if (connection?.saveData) return false;
    return connection?.effectiveType !== "slow-2g" && connection?.effectiveType !== "2g";
  },
};

export function createPreloader(scheduler: Scheduler = browserScheduler) {
  const loaders = new Map<string, Loader[]>();
  const loaded = new Set<string>();
  const queue: string[] = [];
  let running = false;

  const run = (key: string): Promise<void> => {
    loaded.add(key);
    return Promise.all((loaders.get(key) ?? []).map((load) => load())).then(
      () => undefined,
      () => {
        loaded.delete(key);
      },
    );
  };

  const pump = () => {
    if (running || !scheduler.allowed()) return;
    let next = queue.shift();
    while (next !== undefined && loaded.has(next)) next = queue.shift();
    if (next === undefined) return;
    const key = next;
    running = true;
    scheduler.idle(() => {
      void run(key).then(() => {
        running = false;
        pump();
      });
    });
  };

  return {
    register(key: string, ...load: Loader[]): void {
      loaders.set(key, load);
    },
    now(key: string): void {
      if (!loaded.has(key) && loaders.has(key)) void run(key);
    },
    queue(keys: readonly string[], opts: { front?: boolean } = {}): void {
      const fresh = keys.filter((key) => !loaded.has(key) && loaders.has(key));
      for (const key of fresh) {
        const at = queue.indexOf(key);
        if (at >= 0) queue.splice(at, 1);
      }
      if (opts.front) queue.unshift(...fresh);
      else queue.push(...fresh);
      pump();
    },
    pending(): readonly string[] {
      return queue;
    },
  };
}

export const sectionPreloader = createPreloader();

export const workspacePreloadKey = (workspace: string) => `workspace:${workspace}`;
export const workspaceDataKey = (workspace: string) => `data:${workspace}`;

export function preloadWorkspace(workspace: string): void {
  sectionPreloader.now(workspacePreloadKey(workspace));
  sectionPreloader.now(workspaceDataKey(workspace));
}
